import { adaptPdfDownload, type PdfDownload } from '~/@entities/PodCart'
import type { KmmProduct } from '~/@entities/PodProduct'
import type { PaymentData, PodPaymentRequest } from '~/api-spec/__generated__/main'
import type {
  CheckPaymentTokenData,
  CheckPaymentTokenParams,
  CreateOrderData,
  CreatePreOrderData,
  DownloadOrderItemPdfFileV2Data,
  FindOrderData,
  FindOrderParams,
  GetDeliveryLeadTimeData,
  GetGroupOrderData,
  GetGroupOrderDetail1Data,
  GetOrderData,
  GetOrderItemData,
  GetPaymentInfoV2Data,
  GetPaymentInfoV2Params,
  GetPreOrderData,
  GetPreOrderProductDiscountEventData,
  GetPreOrderUserCouponData,
  GetPreOrderUserCouponParams,
  GetPrintOptionData,
  GetUserCouponData,
  GetUserCouponParams,
  GetUserData,
  GetUserProductDiscountEventData,
  OfflinePaymentData,
  Pageable,
  ParentOrderCreationRequest,
  ParentPaymentOfflinePaymentRequest,
  ParentPaymentRequest,
  ParentPaymentResendRequest,
  PreOrderCreationRequest,
  PreOrderPointUpdateRequest,
  PreOrderProductDiscountEventUpdateRequest,
  PreOrderUpdateRequest,
  PreOrderUserCouponUpdateRequest,
  ProductPaymentRequest,
  UpdatePreOrderData,
  UserCouponAllocationRequest,
  UserDefaultDeliveryAddressUpdateRequest,
} from '~/api-spec/__generated__/pod'
import { httpClient } from '~/http/client'
import type { WithNullableOptionals } from '~/http/request'
import type { Page } from '~/schema/page'

import {
  adaptCoupons,
  adaptDeliveryAddress,
  adaptDeliveryAddressBook,
  adaptDeliveryLeadTime,
  adaptDirectOrders,
  adaptKmmOrderCreation,
  adaptKmmOrders,
  adaptOrderItems,
  adaptParentOrderDetail,
  adaptParentOrderPaymentToken,
  adaptParentOrders,
  adaptParentPayment,
  adaptParentPaymentTokenValidity,
  adaptPodUser,
  adaptPreOrder,
  adaptPreOrderCreation,
  adaptPreOrderProductDiscountEvent,
  adaptPrintOption,
  adaptProductDiscountEvent,
} from './adapters'
import type {
  Coupon,
  DeliveryAddress,
  DeliveryAddressBook,
  DeliveryAddressId,
  DeliveryLeadTime,
  DirectOrder,
  KmmOrder,
  OrderItem,
  ParentOrder,
  ParentOrderDetail,
  ParentPayment,
  PodUser,
  PreOrder,
  PreOrderProductDiscountEvent,
  PrintOption,
  ProductDiscountEvent,
} from './schemas'

// 서버는 요청의 선택 필드(배송 메모·이메일·결제 키 등)를 String?·Int? 로 받지만 스펙이 nullable 을 빠뜨려
// optional 로만 그린다. 선택 필드에 null 을 되살린다. 2026-10-06 POD 서버 develop(edfdcec) 의
// order·user·payment/controller/request 와 메인 서버 develop 의 pod/controller/request/PodPaymentRequest.kt 로 확인.
export type PodUserDeliveryAddressUpdate =
  WithNullableOptionals<UserDefaultDeliveryAddressUpdateRequest>
export type PreOrderCreation = PreOrderCreationRequest
// 서버는 받는 사람·연락처·이메일·주소·상세 주소·우편번호를 둘 중 한 이름으로 반드시 받는다(없으면 500)
export type PreOrderDeliveryAddressUpdate = WithNullableOptionals<PreOrderUpdateRequest>
// userCouponId 가 null 이면 적용한 쿠폰을 뺀다
export type PreOrderCouponUpdate = WithNullableOptionals<PreOrderUserCouponUpdateRequest>
export type PreOrderProductDiscountEventUpdate = PreOrderProductDiscountEventUpdateRequest
export type PreOrderPointUpdate = PreOrderPointUpdateRequest
export type CouponListParams = GetUserCouponParams
export type PreOrderCouponListParams = Omit<GetPreOrderUserCouponParams, 'preOrderId'>
export type CouponRegistration = UserCouponAllocationRequest
// 정렬은 `속성,방향` 문자열이다(createDatetime,DESC)
export type OrderListParams = Pageable
export type ParentOrderCreation = ParentOrderCreationRequest
export type ParentOrderResend = WithNullableOptionals<ParentPaymentResendRequest>
export type ParentOrderOfflinePayment = ParentPaymentOfflinePaymentRequest
export type ParentPaymentParams = GetPaymentInfoV2Params & CheckPaymentTokenParams
export type ParentPaymentConfirmation = WithNullableOptionals<ParentPaymentRequest>
// POST /v1/orders 는 커밋된 pod 스펙 스냅샷에 없다(2026-10-19 dev Swagger 에는 있다). 생성 타입을 다시 만들기 전까지
// 서버 요청 모양을 손으로 적어 둔다. 재생성하면 생성 타입에서 파생으로 바꾼다.
export type KmmOrderDelivery = {
  deliveryRecipientName: string
  deliveryRecipientPhoneNumber: string
  deliveryAddress: string
  deliveryAddressDetail: string
  deliveryZipCode: string
  deliveryMemo?: string
}
export type KmmOrderCertificate = {
  studentExamId: number
  quantity: number
  // 1~20자. 서버가 앞뒤 공백을 허용하지 않는다
  studentName: string
}
type KmmCertificateOrderItem = {
  productType: 'KMM_CERTIFICATE'
  productId: number
  // certificates[].quantity 의 합과 같아야 한다(다르면 400)
  quantity: number
  certificates: KmmOrderCertificate[]
}
type KmmAwardsOrderItem = {
  productType: 'KMM_AWARDS'
  productId: number
  quantity: number
  academyMarketingName: string | null
  academyMarketingNumber: string | null
}
// 상장과 배너는 각각 따로 주문하고 KMM_CERTIFICATE 항목은 1개만 담는다
export type KmmOrderCreation = KmmOrderDelivery & {
  orderItems: (KmmCertificateOrderItem | KmmAwardsOrderItem)[]
}
export type KmmPaymentConfirmation = WithNullableOptionals<ProductPaymentRequest>
export type KmmOrderListParams = Omit<FindOrderParams, 'productId'>
export type PreOrderPaymentConfirmation = WithNullableOptionals<PodPaymentRequest>

/***************** 교재몰 사용자 *****************/
export const getPodUser = async (): Promise<PodUser> => {
  const body = await httpClient('pod').get<GetUserData>('/user')
  return adaptPodUser(body)
}

export const updatePodUserDeliveryAddress = async (
  payload: PodUserDeliveryAddressUpdate,
): Promise<void> => {
  await httpClient('pod').put('/user/delivery-address', payload)
}

/***************** 배송지 주소록 *****************/
// /delivery-addresses 는 커밋된 pod 스펙 스냅샷에 없다. 생성 타입을 다시 만들기 전까지 요청 모양을 손으로 적어 둔다.
export type DeliveryAddressDraft = {
  label: string
  zipCode: string
  address: string
  addressDetail: string
}

export const getDeliveryAddressBook = async (): Promise<DeliveryAddressBook> => {
  const body = await httpClient('pod').get<unknown>('/delivery-addresses')
  return adaptDeliveryAddressBook(body)
}

export const createDeliveryAddress = async (
  draft: DeliveryAddressDraft,
): Promise<DeliveryAddress> => {
  const body = await httpClient('pod').post<unknown>('/delivery-addresses', draft)
  return adaptDeliveryAddress(body)
}

export const updateDeliveryAddress = async (
  id: DeliveryAddressId,
  draft: DeliveryAddressDraft,
): Promise<DeliveryAddress> => {
  const body = await httpClient('pod').put<unknown>(`/delivery-addresses/${id}`, draft)
  return adaptDeliveryAddress(body)
}

export const deleteDeliveryAddress = async (id: DeliveryAddressId): Promise<void> => {
  await httpClient('pod').delete(`/delivery-addresses/${id}`)
}

// 진행 중인 할인 이벤트가 없으면 서버가 404 로 답한다
export const getProductDiscountEvent = async (): Promise<ProductDiscountEvent> => {
  const body = await httpClient('pod').get<GetUserProductDiscountEventData>(
    '/user/product-discount-event',
  )
  return adaptProductDiscountEvent(body)
}

/***************** 주문서 *****************/
export const getPreOrder = async (id: PreOrder['id']): Promise<PreOrder> => {
  const body = await httpClient('pod').get<GetPreOrderData>('/pre-order', { params: { id } })
  return adaptPreOrder(body)
}

export const createPreOrder = async (payload: PreOrderCreation): Promise<PreOrder['id']> => {
  const body = await httpClient('pod').post<CreatePreOrderData>('/pre-order', payload)
  return adaptPreOrderCreation(body)
}

export const updatePreOrderDeliveryAddress = async (
  payload: PreOrderDeliveryAddressUpdate,
): Promise<PreOrder> => {
  const body = await httpClient('pod').put<UpdatePreOrderData>(
    '/pre-order/delivery-address',
    payload,
  )
  return adaptPreOrder(body)
}

export const getDeliveryLeadTime = async (): Promise<DeliveryLeadTime> => {
  const body = await httpClient('pod').get<GetDeliveryLeadTimeData>('/pre-order/delivery-lead-time')
  return adaptDeliveryLeadTime(body)
}

export const updatePreOrderPoint = async (
  preOrderId: PreOrder['id'],
  payload: PreOrderPointUpdate,
): Promise<void> => {
  await httpClient('pod').put(`/pre-order/${preOrderId}/point`, payload)
}

// 할인 이벤트가 없거나 주문서에 맞춤 인쇄물이 있으면 서버가 404 로 답한다
export const getPreOrderProductDiscountEvent = async (
  preOrderId: PreOrder['id'],
): Promise<PreOrderProductDiscountEvent> => {
  const body = await httpClient('pod').get<GetPreOrderProductDiscountEventData>(
    `/pre-order/${preOrderId}/product-discount-event`,
  )
  return adaptPreOrderProductDiscountEvent(body)
}

export const updatePreOrderProductDiscountEvent = async (
  preOrderId: PreOrder['id'],
  payload: PreOrderProductDiscountEventUpdate,
): Promise<void> => {
  await httpClient('pod').put(`/pre-order/${preOrderId}/product-discount-event`, payload)
}

/***************** 쿠폰 *****************/
export const getCoupons = async (params?: CouponListParams): Promise<Coupon[]> => {
  const body = await httpClient('pod').get<GetUserCouponData>('/user-coupon', { params })
  return adaptCoupons(body)
}

export const getPreOrderCoupons = async (
  preOrderId: PreOrder['id'],
  params?: PreOrderCouponListParams,
): Promise<Coupon[]> => {
  const body = await httpClient('pod').get<GetPreOrderUserCouponData>(
    `/pre-order/${preOrderId}/user-coupon`,
    { params },
  )
  return adaptCoupons(body)
}

export const updatePreOrderCoupon = async (payload: PreOrderCouponUpdate): Promise<void> => {
  await httpClient('pod').put('/pre-order/user-coupon', payload)
}

export const registerCoupon = async (
  couponId: Coupon['id'],
  payload: CouponRegistration,
): Promise<void> => {
  await httpClient('pod').post(`/user-coupon/${couponId}`, payload)
}

/***************** 주문 *****************/
export const getDirectOrders = async (params: OrderListParams): Promise<Page<DirectOrder>> => {
  const body = await httpClient('pod').get<GetOrderData>('/order', { params })
  return adaptDirectOrders(body)
}

export const getOrderItems = async (orderId: DirectOrder['id']): Promise<OrderItem[]> => {
  const body = await httpClient('pod').get<GetOrderItemData>(`/order/${orderId}/item`)
  return adaptOrderItems(body)
}

export const cancelOrder = async (orderId: DirectOrder['id']): Promise<void> => {
  await httpClient('pod').put(`/order/${orderId}/cancel`)
}

export const getOrderItemPdfDownload = async (
  orderItemId: OrderItem['id'],
): Promise<PdfDownload> => {
  const body = await httpClient('pod').get<DownloadOrderItemPdfFileV2Data>(
    `/order/v2/download/${orderItemId}`,
  )
  return adaptPdfDownload(body)
}

export const getPrintOption = async (): Promise<PrintOption> => {
  const body = await httpClient('pod').get<GetPrintOptionData>('/print/option')
  return adaptPrintOption(body)
}

/***************** 학부모 결제 *****************/
export const getParentOrders = async (params: OrderListParams): Promise<Page<ParentOrder>> => {
  const body = await httpClient('pod').get<GetGroupOrderData>('/parent-order/v2', { params })
  return adaptParentOrders(body)
}

export const getParentOrderDetail = async (
  groupOrderId: ParentOrder['groupOrderId'],
): Promise<ParentOrderDetail> => {
  const body = await httpClient('pod').get<GetGroupOrderDetail1Data>(
    `/parent-order/v2/${groupOrderId}`,
  )
  return adaptParentOrderDetail(body)
}

// 학생별 학부모에게 결제 요청 문자를 보낸다
export const createParentOrders = async (payload: ParentOrderCreation): Promise<void> => {
  await httpClient('pod').post('/parent-order/v2', payload)
}

export const resendParentOrder = async (payload: ParentOrderResend): Promise<void> => {
  await httpClient('pod').put('/parent-order/resend', payload)
}

// 학부모 결제 요청을 선생님 현장 결제로 바꾸고 그 주문의 결제 토큰을 돌려받는다
export const payParentOrderOffline = async (
  payload: ParentOrderOfflinePayment,
): Promise<string> => {
  const body = await httpClient('pod').put<OfflinePaymentData>(
    '/parent-order/offline-payment',
    payload,
  )
  return adaptParentOrderPaymentToken(body)
}

export const checkParentPaymentToken = async (params: ParentPaymentParams): Promise<boolean> => {
  const body = await httpClient('pod').get<CheckPaymentTokenData>(
    '/parent-payment/check-payment-token',
    { params },
  )
  return adaptParentPaymentTokenValidity(body)
}

export const getParentPayment = async (params: ParentPaymentParams): Promise<ParentPayment> => {
  const body = await httpClient('pod').get<GetPaymentInfoV2Data>('/parent-payment/v2', { params })
  return adaptParentPayment(body)
}

/** 주문서 결제를 확정한다. 메인 서버(/pod/payment)가 무료 체험 여부를 더해 교재몰 서버로 넘긴다 */
export const confirmPreOrderPayment = async (
  payload: PreOrderPaymentConfirmation,
): Promise<void> => {
  await httpClient().post<PaymentData>('/pod/payment', payload)
}

export const confirmParentPayment = async (payload: ParentPaymentConfirmation): Promise<void> => {
  await httpClient('pod').post('/parent-payment', payload)
}

/***************** KMM 시상품 주문 *****************/
export const createKmmOrder = async (payload: KmmOrderCreation): Promise<KmmOrder['id']> => {
  const body = await httpClient('pod').post<CreateOrderData>('/v1/orders', payload)
  return adaptKmmOrderCreation(body)
}

export const confirmKmmPayment = async (payload: KmmPaymentConfirmation): Promise<void> => {
  await httpClient('pod').post('/payment/without-pre-order', payload)
}

export const getKmmOrders = async (
  productId: KmmProduct['id'],
  params?: KmmOrderListParams,
): Promise<KmmOrder[]> => {
  const body = await httpClient('pod').get<FindOrderData>(`/kmm/order/${productId}`, { params })
  return adaptKmmOrders(body)
}
